import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Send, User, Mail, MessageSquare, AlertCircle } from 'lucide-react';
import { ThemeColors, Spacing, Typography, BorderRadius, Transitions } from '../../styles/ThemeStyles';
import { EmailService } from '../../services/EmailService';
import toast from 'react-hot-toast';

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
`;

const Label = styled.label`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

const Input = styled.input`
  padding: ${Spacing.md};
  border: 1px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.md};
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${ThemeColors.primary.light}33;
  }
`;

const TextArea = styled.textarea`
  padding: ${Spacing.md};
  border: 1px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.md};
  min-height: 120px;
  resize: vertical;
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${ThemeColors.primary.light}33;
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.sm};
  background-color: ${ThemeColors.primary.main};
  color: white;
  padding: ${Spacing.md};
  border: none;
  border-radius: ${BorderRadius.md};
  font-weight: ${Typography.fontWeight.bold};
  cursor: pointer;
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};

  &:hover:not(:disabled) {
    background-color: ${ThemeColors.primary.dark};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const InfoText = styled.p`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
  margin-top: ${Spacing.sm};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

interface SupportFormProps {
  userEmail?: string;
  userName?: string;
  onSuccess?: () => void;
}

export const SupportForm: React.FC<SupportFormProps> = ({ 
  userEmail = '', 
  userName = '',
  onSuccess 
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: userName,
    email: userEmail,
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.message.trim() || !formData.subject.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const emailBody = `
        <h3>Technical Support Request</h3>
        <p><strong>From:</strong> ${formData.name} (${formData.email})</p>
        <p><strong>Subject:</strong> ${formData.subject}</p>
        <p><strong>Message:</strong></p>
        <p style="white-space: pre-wrap;">${formData.message}</p>
        <hr />
        <p><small>Sent from McKenzie Portal Technical Support Form</small></p>
      `;

      const result = await EmailService.sendEmail({
        to: 'devbob.co@gmail.com',
        subject: `[SUPPORT] ${formData.subject}`,
        body: emailBody
      });

      if (result.success) {
        toast.success('Support request sent successfully!');
        setFormData({ ...formData, subject: '', message: '' });
        if (onSuccess) onSuccess();
      } else {
        throw new Error(result.error);
      }
    } catch (error: any) {
      console.error('Failed to send support request:', error);
      toast.error(error.message || 'Failed to send support request. Please try again or call support.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormContainer onSubmit={handleSubmit}>
      <FormGroup>
        <Label htmlFor="name"><User size={16} /> Name</Label>
        <Input 
          id="name"
          type="text" 
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Your name"
          required
        />
      </FormGroup>

      <FormGroup>
        <Label htmlFor="email"><Mail size={16} /> Email</Label>
        <Input 
          id="email"
          type="email" 
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="Your email address"
          required
        />
      </FormGroup>

      <FormGroup>
        <Label htmlFor="subject"><AlertCircle size={16} /> Subject</Label>
        <Input 
          id="subject"
          type="text" 
          value={formData.subject}
          onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
          placeholder="What is the issue?"
          required
        />
      </FormGroup>

      <FormGroup>
        <Label htmlFor="message"><MessageSquare size={16} /> Message</Label>
        <TextArea 
          id="message"
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          placeholder="Describe the technical issue in detail..."
          required
        />
      </FormGroup>

      <SubmitButton type="submit" disabled={isSubmitting}>
        <Send size={18} />
        {isSubmitting ? 'Sending...' : 'Send Support Request'}
      </SubmitButton>

      <InfoText>
        <AlertCircle size={12} />
        Our technical team will get back to you as soon as possible.
      </InfoText>
    </FormContainer>
  );
};

